// Browser Offline Transcriber with IndexedDB Storage
import { pipeline, env } from '@huggingface/transformers';
import { getBlobEntry } from './_db';
import levenshtein from 'js-levenshtein';
//import wasm from "vite-plugin-wasm";
import { doubleMetaphone } from 'double-metaphone';
// Configure transformers.js for offline use
env.allowRemoteModels = false;
env.allowLocalModels = true;
//env.backends.onnx.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.14.0/dist/';

const STORE_NAME = 'model';

// Global transcriber instance
let transcriber = null;
let isInitializing = false;



// Custom model cache that uses IndexedDB
class IndexedDBModelCache {
    constructor() {
        this.memoryCache = new Map();
    }
    
    async get(fileName) {
        // Check memory cache first
        if (this.memoryCache.has(fileName)) {
            console.log(`📋 Found ${fileName} in memory cache`);
            return this.memoryCache.get(fileName);
        }
        
        // Check IndexedDB using the exact filename as stored
        try {
            const data = await getBlobEntry(fileName);
            if (data) {
                // Store in memory cache for faster access
                this.memoryCache.set(fileName, data);
                console.log(`💾 Loaded ${fileName} from IndexedDB to memory cache`);
                return data;
            }
        } catch (error) {
            console.error(`Error retrieving ${fileName} from IndexedDB:`, error);
        }
        
        return null;
    }
    
    // Method to preload commonly needed files
    async preloadFiles(fileNames) {
        const loadPromises = fileNames.map(async (fileName) => {
            try {
                const data = await this.get(fileName);
                if (data) {
                    console.log(`✅ Preloaded ${fileName}`);
                } else {
                    console.warn(`⚠️ Could not preload ${fileName}`);
                }
            } catch (error) {
                console.error(`❌ Error preloading ${fileName}:`, error);
            }
        });
        
        await Promise.all(loadPromises);
    }
}

const modelCache = new IndexedDBModelCache();

// Override transformers.js file system to use IndexedDB
function setupCustomFileSystem() {
    const originalFetch = globalThis.fetch;
    
    globalThis.fetch = async (url, options) => {
        try {
            // Check if this is a model file request by looking for common model file extensions
            if (typeof url === 'string') {
                const urlParts = url.split('/');
                const fileName = urlParts[urlParts.length - 1];
                
                // Check if this looks like a model file
                const modelFileExtensions = ['.json', '.onnx', '.bin', '.safetensors'];
                const isModelFile = modelFileExtensions.some(ext => fileName.includes(ext)) || 
                                  fileName.includes('config') || 
                                  fileName.includes('tokenizer') ||
                                  fileName.includes('generation') ||
                                  fileName.includes('preprocessor') ||
                                  fileName.includes('model');
                
                if (isModelFile) {
                    console.log(`🔍 Looking for ${fileName} in IndexedDB...`);
                    
                    // Try to get the file from IndexedDB using exact filename
                    const data = await modelCache.get(fileName);
                    if (data) {
                        console.log(`✅ Found ${fileName} in IndexedDB`);
                        
                        // Determine content type based on file extension
                        let contentType = 'application/octet-stream';
                        if (fileName.endsWith('.json')) {
                            contentType = 'application/json';
                        } else if (fileName.endsWith('.onnx')) {
                            contentType = 'application/octet-stream';
                        }
                        
                        return new Response(data, {
                            status: 200,
                            headers: { 
                                'Content-Type': contentType,
                                'Content-Length': data.size.toString()
                            }
                        });
                    } else {
                        console.log(`❌ ${fileName} not found in IndexedDB, falling back to network`);
                    }
                }
            }
            
            // Fallback to original fetch
            return await originalFetch(url, options);
        } catch (error) {
            console.error('Custom fetch error:', error);
            // If our custom logic fails, fallback to original fetch
            return await originalFetch(url, options);
        }
    };
}

// Initialize the transcriber with IndexedDB support
// Initialize the transcriber with IndexedDB support
async function initializeTranscriber(modelId = 'Xenova/whisper-base.en', baseUrl = null) {
    if (isInitializing) {
        console.log('⏳ Transcriber is already initializing...');
        // Wait for current initialization to complete
        while (isInitializing) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        return transcriber;
    }
    
    if (transcriber) {
        console.log('✅ Transcriber already loaded');
        return transcriber;
    }
    
    isInitializing = true;
    
    try {
        console.log(`🔄 Initializing transcriber: ${modelId}`);
        
        // Setup custom file system to use IndexedDB
        setupCustomFileSystem();
        
        // Preload commonly needed model files to memory cache
        const commonFiles = [
            'config.json',
            'generation_config.json',
            'preprocessor_config.json',
            'tokenizer_config.json',
            'tokenizer.json',
            'decoder_model_merged_quantized.onnx',
            'encoder_model_quantized.onnx'
        ];
        
        console.log('📦 Preloading model files to memory cache...');
        await modelCache.preloadFiles(commonFiles);
        
        // Load the model
        console.log('🤖 Loading transcriber pipeline...');
        transcriber = await pipeline('automatic-speech-recognition', modelId);
        
        console.log(`✅ Transcriber loaded successfully`);
        return transcriber;
        
    } catch (error) {
        console.error(`❌ Failed to initialize transcriber:`, error);
        transcriber = null; // Reset on failure
        throw error;
    } finally {
        isInitializing = false;
    }
}

// Convert audio blob to audio buffer (Web Audio API)
async function convertAudioBlobToBuffer(audioBlob) {
    try {
        console.log('🔄 Converting audio blob to buffer...');
        
        // Convert blob to array buffer
        const arrayBuffer = await audioBlob.arrayBuffer();
        
        // Create audio context for processing
        const audioContext = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 16000
        });
        
        // Decode audio data
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        // Get the first channel (mono)
        const channelData = audioBuffer.getChannelData(0);
        
        // Convert to Float32Array (required format for Whisper)
        const samples = new Float32Array(channelData);
        
        // Close audio context to free resources
        await audioContext.close();
        
        console.log(`✅ Audio converted: ${samples.length} samples at 16kHz`);
        return samples;
        
    } catch (error) {
        console.error('❌ Error converting audio blob:', error);
        throw new Error('Failed to process audio blob: ' + error.message);
    }
}

// Alternative audio conversion method for better compatibility
async function convertAudioBlobToBufferCompatible(audioBlob) {
    try {
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audio = new Audio();
        const url = URL.createObjectURL(audioBlob);
        
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                URL.revokeObjectURL(url);
                reject(new Error('Audio loading timeout'));
            }, 10000);
            
            audio.addEventListener('loadeddata', async () => {
                try {
                    clearTimeout(timeout);
                    
                    const audioContext = new (window.AudioContext || window.webkitAudioContext)({
                        sampleRate: 16000
                    });
                    
                    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                    const channelData = audioBuffer.getChannelData(0);
                    const samples = new Float32Array(channelData);
                    
                    URL.revokeObjectURL(url);
                    await audioContext.close();
                    resolve(samples);
                } catch (err) {
                    clearTimeout(timeout);
                    URL.revokeObjectURL(url);
                    reject(err);
                }
            });
            
            audio.addEventListener('error', (err) => {
                clearTimeout(timeout);
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load audio: ' + err.message));
            });
            
            audio.src = url;
        });
    } catch (error) {
        console.error('Error in compatible audio conversion:', error);
        throw error;
    }
}

// Function to check if transcription matches base word
function isCorrect(transcription, baseWord, options = {}) {
    const {
        levenshteinWeight = 0.6,
        metaphoneWeight = 0.4,
        threshold = 0.4,
        removeSpaces = true
    } = options;
    
    // Normalize inputs
    let normalizedTranscription = transcription.toLowerCase().trim();
    let normalizedBaseWord = baseWord.toLowerCase().trim();
    
    if (removeSpaces) {
        normalizedTranscription = normalizedTranscription.replace(/\s+/g, '');
        normalizedBaseWord = normalizedBaseWord.replace(/\s+/g, '');
    }
    
    // Calculate Levenshtein similarity
    const levDist = levenshtein(normalizedTranscription, normalizedBaseWord);
    const maxLen = Math.max(normalizedTranscription.length, normalizedBaseWord.length);
    const levSimilarity = maxLen === 0 ? 1 : 1.0 - levDist / maxLen;
    
    // Calculate Double Metaphone similarity
    const transcriptionMeta = doubleMetaphone(normalizedTranscription);
    const baseWordMeta = doubleMetaphone(normalizedBaseWord);
    
    let metaphoneSimilarity = 0;
    if (transcriptionMeta[0] === baseWordMeta[0] || 
        transcriptionMeta[0] === baseWordMeta[1] ||
        transcriptionMeta[1] === baseWordMeta[0] || 
        transcriptionMeta[1] === baseWordMeta[1]) {
        metaphoneSimilarity = 1.0;
    } else {
        const meta1Sim = transcriptionMeta[0] && baseWordMeta[0] ? 
            1.0 - levenshtein(transcriptionMeta[0], baseWordMeta[0]) / 
            Math.max(transcriptionMeta[0].length, baseWordMeta[0].length) : 0;
        const meta2Sim = transcriptionMeta[1] && baseWordMeta[1] ? 
            1.0 - levenshtein(transcriptionMeta[1], baseWordMeta[1]) / 
            Math.max(transcriptionMeta[1].length, baseWordMeta[1].length) : 0;
        metaphoneSimilarity = Math.max(meta1Sim, meta2Sim);
    }
    
    const weightedSimilarity = (levSimilarity * levenshteinWeight) + 
                              (metaphoneSimilarity * metaphoneWeight);
    
    return {
        isMatch: weightedSimilarity > threshold,
        similarity: weightedSimilarity,
        levenshteinSimilarity: levSimilarity,
        metaphoneSimilarity: metaphoneSimilarity,
        details: {
            transcriptionMetaphone: transcriptionMeta,
            baseWordMetaphone: baseWordMeta,
            normalizedTranscription,
            normalizedBaseWord
        }
    };
}

// Main transcribe function - This is what gets exported and called from other files
export async function transcribe(audioBlob, baseWord, options = {}) {
    try {
        // Validate inputs
        if (!audioBlob || !(audioBlob instanceof Blob)) {
            throw new Error('Invalid audio blob provided. Expected Blob object.');
        }
        
        if (!baseWord || typeof baseWord !== 'string') {
            throw new Error('Invalid base word provided. Expected non-empty string.');
        }
        
        const {
            modelId = 'Xenova/whisper-base.en',
            baseUrl = null,
            threshold = 0.4
        } = options;
        
        console.log(`🎤 Starting transcription for base word: "${baseWord}"`);
        
        // Initialize transcriber if not already done
        if (!transcriber && !isInitializing) {
            console.log('🔄 Initializing transcriber...');
            await initializeTranscriber(modelId, baseUrl);
        }
        
        // Wait for initialization if in progress
        while (isInitializing) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (!transcriber) {
            throw new Error('Failed to initialize transcriber');
        }
        
        // Convert audio blob to required format
        let audioData;
        try {
            audioData = await convertAudioBlobToBuffer(audioBlob);
        } catch (error) {
            console.warn('⚠️ Primary conversion failed, trying compatible method...');
            audioData = await convertAudioBlobToBufferCompatible(audioBlob);
        }
        
        // Transcribe the audio
        console.log('🔄 Transcribing audio...');
        const result = await transcriber(audioData);
        const transcription = result.text.toLowerCase().trim();
        
        console.log(`📝 Transcription result: "${transcription}"`);
        
        // Check correctness against base word
        const correctnessResult = isCorrect(transcription, baseWord, options);
        
        const finalResult = {
            success: true,
            transcription: transcription,
            baseWord: baseWord,
            isCorrect: correctnessResult.isMatch,
            similarity: Math.round(correctnessResult.similarity * 100) / 100,
            levenshteinSimilarity: Math.round(correctnessResult.levenshteinSimilarity * 100) / 100,
            metaphoneSimilarity: Math.round(correctnessResult.metaphoneSimilarity * 100) / 100,
            details: correctnessResult.details
        };
        
        console.log('✅ Transcription completed:', finalResult);
        return finalResult;
        
    } catch (error) {
        console.error('❌ Transcription error:', error);
        return {
            success: false,
            error: error.message,
            transcription: null,
            baseWord: baseWord,
            isCorrect: false,
            similarity: 0
        };
    }
}

// Export the main function and utilities
export {
    transcribe as default,
};
